import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@reflex/ui/components/ui/dropdown-menu';
import { KbdCombo } from '@reflex/ui/components/shared/Kbd';
import { cn } from '@reflex/ui/lib/utils';
import type { AgentAction, AgentDialogAction } from './useAgentActions';

function ActionShortcut({ keys }: { keys: readonly string[] }) {
  return (
    <DropdownMenuShortcut className="tracking-normal">
      <KbdCombo keys={[...keys]} size="xs" />
    </DropdownMenuShortcut>
  );
}

function ActionLabel({ action }: { action: AgentAction }) {
  return (
    <>
      <action.icon className={cn('mr-2 h-4 w-4', action.iconClassName)} />
      {action.label}
      {action.shortcut ? <ActionShortcut keys={action.shortcut} /> : null}
    </>
  );
}

function itemClass(action: AgentAction): string | undefined {
  return cn(action.destructive && 'text-destructive focus:text-destructive');
}

export interface AgentActionMenuItemsProps {
  actions: AgentAction[];
  /**
   * Called when a dialog-kind action is selected. The dialog itself is
   * rendered by the parent (via `AgentActionDialogs`) outside the dropdown
   * content, so it survives Radix unmounting the menu after the click.
   */
  onSelectDialog: (action: AgentDialogAction) => void;
  /** Extra classes applied to every menu item (e.g. `md:hidden` for mobile-only). */
  className?: string;
}

/**
 * Renders shared {@link AgentAction}s as dropdown menu items for the agent
 * header overflow menu. A separator only precedes the danger group (Delete)
 * so ordinary actions stay a continuous list. Dialog actions delegate
 * opening to `onSelectDialog` — the parent must render `AgentActionDialogs`
 * as a sibling of the dropdown so the modal isn't unmounted when the menu
 * closes.
 */
export function AgentActionMenuItems({
  actions,
  onSelectDialog,
  className,
}: AgentActionMenuItemsProps) {
  return (
    <>
      {actions.map((action, index) => {
        const prev = actions[index - 1];
        const showSeparator = prev && prev.group !== 'danger' && action.group === 'danger';
        const itemCn = cn(itemClass(action), className);
        return (
          <Fragment key={action.key}>
            {showSeparator ? <DropdownMenuSeparator className={className} /> : null}
            {action.kind === 'submenu' ? (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className={itemCn}>
                  <action.icon className={cn('mr-2 h-4 w-4', action.iconClassName)} />
                  {action.label}
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {action.items.map((item) => (
                    <DropdownMenuItem key={item.key} onSelect={item.onSelect}>
                      <item.icon className={cn('mr-2 h-4 w-4', item.iconClassName)} />
                      {item.label}
                      {item.checked && <Check className="ml-auto h-4 w-4" />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            ) : action.kind === 'dialog' ? (
              <DropdownMenuItem
                className={itemCn}
                onSelect={() => onSelectDialog(action)}
                data-replay-target={action.replayTarget}
              >
                <ActionLabel action={action} />
              </DropdownMenuItem>
            ) : action.kind === 'link' ? (
              <DropdownMenuItem asChild className={itemCn}>
                <Link to={action.to}>
                  <ActionLabel action={action} />
                </Link>
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                className={itemCn}
                onClick={action.onSelect}
                data-replay-target={action.replayTarget}
              >
                <ActionLabel action={action} />
              </DropdownMenuItem>
            )}
          </Fragment>
        );
      })}
    </>
  );
}
